import axios from 'axios'
import { useAuthStore } from '@/stores/auth.store.ts'
import { handleApiError } from '@/shared/handleApiError'
import type { NotificationCount, NotificationModel } from '@/types/notification.model'
import type { Result } from '@/types/result.ts'
import type { ApiError } from '@/types/errors/apiError.ts'
import type { PaginatedResult } from '@/types/paginatedResult.model'

const VITE_ENDPOINT_NOTIFICATION =
  import.meta.env.VITE_API_URL + import.meta.env.VITE_ENDPOINT_NOTIFICATION

/**
 * Fetches the notifications associated with the authenticated user.
 * @returns A promise that resolves to a @class Result containing either an array of NotificationModel or an ApiError.
 */
export async function getNotifications(
  page: number,
  pageSize: number,
): Promise<Result<PaginatedResult<NotificationModel>, ApiError>> {
  const auth = useAuthStore()
  const accessToken = await auth.getToken()

  try {
    const response = await axios.get(VITE_ENDPOINT_NOTIFICATION, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        page,
        pageSize,
      },
    })

    return { success: true, data: response.data }
  } catch (e: unknown) {
    const apiError = handleApiError(e)
    return { success: false, error: apiError }
  }
}

/**
 * Marks a notification as read
 * @param notificationId The ID of the notification to mark as read.
 * @returns A promise that resolves to a @class Result indicating success or containing an ApiError.
 */
export async function markNotificationAsRead(
  notificationId: string,
): Promise<Result<void, ApiError>> {
  const auth = useAuthStore()
  const accessToken = await auth.getToken()

  try {
    await axios.patch(
      `${VITE_ENDPOINT_NOTIFICATION}?notificationId=${notificationId}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return { success: true, data: undefined }
  } catch (e: unknown) {
    const apiError = handleApiError(e)
    return { success: false, error: apiError }
  }
}

/**
 * Deletes a notification by its id based on the authenticated user
 * @param notificationId The ID of the notification to delete.
 * @returns A promise that resolves to a @class Result indicating success or containing an ApiError.
 */
export async function deleteNotification(notificationId: string): Promise<Result<void, ApiError>> {
  const auth = useAuthStore()
  const accessToken = await auth.getToken()

  try {
    await axios.delete(`${VITE_ENDPOINT_NOTIFICATION}?notificationId=${notificationId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return { success: true, data: undefined }
  } catch (e: unknown) {
    const apiError = handleApiError(e)
    return { success: false, error: apiError }
  }
}

/**
 * Fetches the number of unread notification of the currently authenticated user.
 * @returns A promise with the number of unread notifications or an ApiError
 */
export async function getUnreadNotificationCount(): Promise<Result<NotificationCount, ApiError>> {
  const auth = useAuthStore()
  const accessToken = await auth.getToken()

  try {
    const response = await axios.get(VITE_ENDPOINT_NOTIFICATION + '/count', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return { success: true, data: response.data }
  } catch (e: unknown) {
    const apiError = handleApiError(e)
    return { success: false, error: apiError }
  }
}
